import { SpeedyFlowInputs, SpeedyFlowResolution } from '../types';
import { satisfiesGate } from './entitlement';
import selectProductPair from './products';

/**
 * Which flow runs, from which step, offering which products — or nothing at all.
 *
 * Pure: it reads no store, no `window`, and no clock it was not given. That constraint is what
 * makes every combination of link, gate and access state affordable to cover, and it is also what
 * keeps this half portable across apps (ADR 0005).
 */
const resolveSpeedyFlow = ({
  gate,
  isPartnerCheckoutReturn,
  ppt,
  habitatId,
  user,
  now = Date.now(),
}: SpeedyFlowInputs): SpeedyFlowResolution => {
  const gateSatisfied = satisfiesGate(gate, user, now);
  const effectivePpt = gate.ppt ?? ppt;

  if (gateSatisfied) {
    // The one inbound link that still shows something to someone who needs nothing: a partner
    // referral who already holds what the partner sold them gets told so, rather than upsold it.
    if (isPartnerCheckoutReturn) {
      return { flow: 'welcome', initialStep: 'completion', completionVariant: 'partner-welcome' };
    }
    // An explicit `ppt` — on the link or on the gate — is a request to show the offer, and it wins
    // over the gate being satisfied: setting `ppt` is how a link says "show them this". Whether the
    // visitor may actually buy what is offered is not decided here; each card reads its own
    // `eligibility.allowed` from the API and disables itself. `general` names no offer, it only
    // asks for the history-based default, so it does not count as a request.
    if (!effectivePpt || effectivePpt === 'general') {
      return { flow: 'none' };
    }
  }

  if (!gateSatisfied && gate.kind === 'account') {
    return { flow: 'sign-in', initialStep: 'persona' };
  }

  // The one gate whose visitor may already be signed in and still owe something. Someone who is
  // signed in owes only the verification, so the flow opens on that step; someone who is not needs
  // the account first, and verification comes as part of making one.
  if (!gateSatisfied && gate.kind === 'verified-email') {
    return { flow: 'sign-in', initialStep: user.isLoggedIn ? 'verify-email' : 'persona' };
  }

  // The gate already knows the purchase — "purchase again", a resumed pick, a CTA that names the
  // pass it sells — so the product step is skipped for everyone: a member goes to pay, a guest
  // signs up first (ADR 0010).
  //
  // The products resolve anyway. Skipping the step is not the same as it being empty: the refusal
  // events land on `choose-product`, which a guest can reach having never seen it.
  /**
   * Confirm the address before taking money, whichever purchase this is.
   *
   * The guest path already did: verification comes with making the account, so they reach the
   * payment step verified. Someone who signed up earlier and never finished did not, and went
   * straight to checkout — buying a pass against an address nobody can reach, and no receipt they
   * can recover it with. `afterAuth` puts them back on the purchase once it is done.
   */
  const owesVerification = user.isLoggedIn && !user.isRealEmail;

  if (gate.product) {
    if (user.isLoggedIn) {
      return {
        flow: 'member-purchase',
        initialStep: owesVerification ? 'verify-email' : 'await-payment',
        ...selectProductPair(effectivePpt, habitatId ?? gate.habitatId, user, now),
      };
    }
    // `sign-up`, not `await-payment`: nobody to bill yet. Not a new state either — `choseProduct`
    // already routes here when there is no account, so this pre-applies an event `machine.ts` has
    // always supported, and `afterAuth` picks the choice back up.
    return {
      flow: 'guest-purchase',
      initialStep: 'sign-up',
      ...selectProductPair(effectivePpt, habitatId ?? gate.habitatId, user, now),
    };
  }

  return {
    flow: user.isLoggedIn ? 'member-purchase' : 'guest-purchase',
    initialStep: owesVerification ? 'verify-email' : 'choose-product',
    ...selectProductPair(effectivePpt, habitatId ?? gate.habitatId, user, now),
  };
};

export default resolveSpeedyFlow;
